import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';

const API = '/api';
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : 'N/A';
const initials = name => (name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
const StatBox = ({ label, value, color }) => (
  <div style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:'14px 16px', textAlign:'center' }}>
    <div style={{ fontSize:22, fontWeight:700, color: color||'#4B9EF5' }}>{value ?? 0}</div>
    <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:4 }}>{label}</div>
  </div>
);

function ClubModal({ id, token, onClose, toast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [acting, setActing] = useState(false);

  useEffect(() => {
    fetch(`${API}/clubs/${id}/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d.success) setData(d.data); }).finally(() => setLoading(false));
  }, [id, token]);

  const doAction = async (status) => {
    setActing(true);
    try {
      const res = await fetch(`${API}/clubs/${id}/status`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const d = await res.json();
      if (d.success) { toast?.(`Club ${status}`); setData(prev => ({ ...prev, verificationStatus: status })); }
      else { toast?.(d.message||'Failed', 'error'); }
    } catch(e) { toast?.(e.message, 'error'); }
    finally { setActing(false); }
  };

  const vsColor = vs => vs==='verified'?'#A7F432':vs==='suspended'?'#ff6b6b':'#F5A623';
  const vsBg    = vs => vs==='verified'?'rgba(167,244,50,0.06)':vs==='suspended'?'rgba(255,80,80,0.06)':'rgba(245,166,35,0.06)';
  const vsBorder= vs => vs==='verified'?'rgba(167,244,50,0.2)':vs==='suspended'?'rgba(255,80,80,0.2)':'rgba(245,166,35,0.2)';

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'flex-start', justifyContent:'center', zIndex:1000, padding:'24px 16px', overflowY:'auto' }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'#0d1a1a', borderRadius:16, width:'100%', maxWidth:800, border:'1px solid rgba(255,255,255,0.07)', marginBottom:32 }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          {loading ? <div style={{ color:'var(--text-dim)' }}>Loading...</div> : data ? (
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:48, height:48, borderRadius:10, background: data.logoUrl?'transparent':'linear-gradient(135deg,#4B9EF5,#1a5db5)', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:700, color:'#fff', flexShrink:0 }}>
                {data.logoUrl ? <img src={data.logoUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : initials(data.clubName)}
              </div>
              <div>
                <div style={{ fontSize:17, fontWeight:700, color:'#fff' }}>{data.clubName}</div>
                <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', marginTop:2 }}>{data.clubCountry}{data.league?` · ${data.league}`:''}{data.city?` · ${data.city}`:''}</div>
              </div>
            </div>
          ) : <div style={{ color:'#ff6b6b' }}>Failed to load</div>}
          <div style={{ display:'flex', gap:8 }}>
            {data && data.verificationStatus !== 'verified' && <button onClick={() => doAction('verified')} disabled={acting} style={{ background:'rgba(167,244,50,0.12)', color:'#A7F432', border:'1px solid rgba(167,244,50,0.3)', borderRadius:8, padding:'6px 14px', fontSize:12, cursor:'pointer', fontWeight:600 }}>Verify</button>}
            {data && data.verificationStatus !== 'suspended' && <button onClick={() => doAction('suspended')} disabled={acting} style={{ background:'rgba(255,80,80,0.12)', color:'#ff6b6b', border:'1px solid rgba(255,80,80,0.3)', borderRadius:8, padding:'6px 14px', fontSize:12, cursor:'pointer', fontWeight:600 }}>Suspend</button>}
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.05)', color:'rgba(255,255,255,0.5)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'6px 12px', fontSize:13, cursor:'pointer' }}>x</button>
          </div>
        </div>
        {loading && <div style={{ padding:60, textAlign:'center', color:'rgba(255,255,255,0.3)' }}>Loading profile...</div>}
        {!loading && data && (
          <>
            <div style={{ margin:'16px 24px 0', background:vsBg(data.verificationStatus), border:`1px solid ${vsBorder(data.verificationStatus)}`, borderRadius:8, padding:'10px 14px', display:'flex', justifyContent:'space-between' }}>
              <span style={{ fontSize:13, color:'rgba(255,255,255,0.6)' }}>Verification Status</span>
              <span style={{ fontSize:12, fontWeight:700, color:vsColor(data.verificationStatus), textTransform:'capitalize' }}>{data.verificationStatus}</span>
            </div>
            <div style={{ padding:'16px 24px', display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
              <StatBox label="Total Events" value={data.stats?.totalEvents} />
              <StatBox label="Player Requests" value={data.stats?.totalRequests} />
              <StatBox label="Affiliated Scouts" value={data.stats?.totalScouts} />
              <StatBox label="Players Pushed" value={data.stats?.playersPushed} color="#F5A623" />
              <StatBox label="Accepted Players" value={data.stats?.acceptedPlayers} color="#A7F432" />
              <StatBox label="Shortlisted" value={data.stats?.shortlisted} color="#9B59B6" />
            </div>
            <div style={{ padding:'0 24px', display:'flex', gap:4, borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
              {['overview','events','requests','scouts','rating'].map(t => (
                <button key={t} onClick={() => setTab(t)} style={{ background:'none', border:'none', cursor:'pointer', padding:'10px 16px', fontSize:12, fontWeight:600, textTransform:'capitalize', color: tab===t?'#4B9EF5':'rgba(255,255,255,0.4)', borderBottom: tab===t?'2px solid #4B9EF5':'2px solid transparent' }}>{t}</button>
              ))}
            </div>
            <div style={{ padding:'20px 24px 24px' }}>
              {tab === 'overview' && (
                <>
                  <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:10, overflow:'hidden', marginBottom:12 }}>
                    {[['Contact Name',data.contactName],['Contact Email',data.contactEmail],['Phone',data.contactPhone],['Club Tier',data.clubTier],['Country',data.clubCountry],['City',data.city],['League',data.league],['Joined',fmtDate(data.createdAt)]].map(([label,value],i,arr) => (
                      <div key={label} style={{ display:'flex', padding:'11px 16px', borderBottom: i<arr.length-1?'1px solid rgba(255,255,255,0.05)':'none' }}>
                        <div style={{ width:160, fontSize:12, color:'rgba(255,255,255,0.4)', flexShrink:0 }}>{label}</div>
                        <div style={{ fontSize:13, color: value?'#fff':'rgba(255,255,255,0.25)' }}>{value||'—'}</div>
                      </div>
                    ))}
                  </div>
                  {data.about && <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:10, padding:'14px 16px', marginBottom:12 }}><div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginBottom:6 }}>About</div><div style={{ fontSize:13, color:'rgba(255,255,255,0.7)', lineHeight:1.6 }}>{data.about}</div></div>}
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    {data.instagramLink && <a href={data.instagramLink} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:'#4B9EF5', textDecoration:'none', background:'rgba(75,158,245,0.1)', padding:'4px 12px', borderRadius:20 }}>Instagram</a>}
                    {data.twitterLink && <a href={data.twitterLink} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:'#4B9EF5', textDecoration:'none', background:'rgba(75,158,245,0.1)', padding:'4px 12px', borderRadius:20 }}>Twitter</a>}
                    {data.websiteLink && <a href={data.websiteLink} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:'#4B9EF5', textDecoration:'none', background:'rgba(75,158,245,0.1)', padding:'4px 12px', borderRadius:20 }}>Website</a>}
                  </div>
                </>
              )}
              {tab === 'events' && (
                <div>{data.events?.length ? data.events.map(ev => (
                  <div key={ev.id} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:'14px 16px', marginBottom:10 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                      <div>
                        <div style={{ fontWeight:600, color:'#fff', fontSize:14 }}>{ev.title}</div>
                        <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', marginTop:4 }}>{ev.eventType} · {(ev.positionsRequired||[]).join(', ')||'Any'} · Age {ev.ageRange||'Any'}</div>
                        <div style={{ fontSize:11, color:'rgba(255,255,255,0.3)', marginTop:4 }}>Trial: {fmtDate(ev.trialDate)} · {ev.numberOfApplicants||0} applicants</div>
                      </div>
                      <span style={{ fontSize:11, padding:'3px 10px', borderRadius:20, background: ev.status==='Active'?'rgba(167,244,50,0.12)':'rgba(255,80,80,0.12)', color: ev.status==='Active'?'#A7F432':'#ff6b6b' }}>{ev.status}</span>
                    </div>
                  </div>
                )) : <div style={{ color:'rgba(255,255,255,0.3)', fontSize:13 }}>No events created</div>}</div>
              )}
              {tab === 'requests' && (
                <div>{data.playerRequests?.length ? data.playerRequests.map(r => (
                  <div key={r.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                    <div>
                      <div style={{ fontSize:13, color:'#fff', fontWeight:500 }}>{r.title}</div>
                      <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:2 }}>{(r.positionsRequired||[]).join(', ')} · Age {r.ageRange||'Any'} · Expires {fmtDate(r.expiresAt)}</div>
                    </div>
                    <span style={{ fontSize:11, padding:'3px 10px', borderRadius:20, background: r.status==='Active'?'rgba(167,244,50,0.12)':'rgba(255,80,80,0.12)', color: r.status==='Active'?'#A7F432':'#ff6b6b' }}>{r.status}</span>
                  </div>
                )) : <div style={{ color:'rgba(255,255,255,0.3)', fontSize:13 }}>No player requests</div>}</div>
              )}
              {tab === 'scouts' && (
                <div>{data.scouts?.length ? data.scouts.map(s => (
                  <div key={s.userId} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ width:36, height:36, borderRadius:'50%', background:'linear-gradient(135deg,#A7F432,#5a9e1a)', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'#000', flexShrink:0 }}>
                      {s.pfp ? <img src={s.pfp} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : initials(`${s.firstName||''} ${s.lastName||''}`)}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, color:'#fff', fontWeight:500 }}>{s.firstName} {s.lastName}</div>
                      <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>{s.email} · {s.nationality||'—'}</div>
                    </div>
                    <div style={{ fontSize:11, color:'#A7F432' }}>Since {fmtDate(s.createdAt)}</div>
                  </div>
                )) : <div style={{ color:'rgba(255,255,255,0.3)', fontSize:13 }}>No affiliated scouts</div>}</div>
              )}
              {tab === 'rating' && (
                <div>
                  {data.rating ? (
                    <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:10, overflow:'hidden', marginBottom:16 }}>
                      {[['Club Tier',data.rating.clubTier],['Total Score',data.rating.totalScore],['League Strength',data.rating.leagueStrengthScore],['Competitive Level',data.rating.competitiveLevelScore],['Development Track',data.rating.developmentTrackRecordScore]].map(([label,value],i,arr) => (
                        <div key={label} style={{ display:'flex', padding:'11px 16px', borderBottom: i<arr.length-1?'1px solid rgba(255,255,255,0.05)':'none' }}>
                          <div style={{ width:180, fontSize:12, color:'rgba(255,255,255,0.4)', flexShrink:0 }}>{label}</div>
                          <div style={{ fontSize:13, color:'#fff', fontWeight: label==='Total Score'?700:400 }}>{value??'—'}</div>
                        </div>
                      ))}
                    </div>
                  ) : <div style={{ color:'rgba(255,255,255,0.3)', fontSize:13, marginBottom:16 }}>No rating data available</div>}
                  {data.membership && (
                    <div style={{ background:'rgba(75,158,245,0.06)', border:'1px solid rgba(75,158,245,0.15)', borderRadius:10, padding:'14px 16px' }}>
                      <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', marginBottom:8 }}>Membership</div>
                      <div style={{ fontSize:14, color:'#fff', fontWeight:600 }}>{data.membership.plan}</div>
                      <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', marginTop:4 }}>{fmtDate(data.membership.startDate)} to {fmtDate(data.membership.endDate)} · {data.membership.status}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Clubs({ toast }) {
  const { token } = useApp();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async (p=1, s='', st='') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page:p, limit:20 });
      if (s) params.set('search', s);
      if (st) params.set('status', st);
      const res = await fetch(`${API}/clubs?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      setClubs(d.data||[]); setPagination(d.pagination||null);
    } catch(e) { toast?.(e.message,'error'); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(1,'',''); }, [load]);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div><h1 style={{ fontFamily:'var(--font-display)', fontSize:22, color:'var(--text-primary)', margin:0 }}>Clubs</h1><p style={{ color:'var(--text-dim)', fontSize:13, margin:'4px 0 0' }}>All registered clubs on the platform</p></div>
        {pagination && <div style={{ fontSize:12, color:'var(--text-dim)', background:'var(--bg-card)', padding:'6px 14px', borderRadius:8, border:'1px solid var(--border)' }}>{pagination.total} clubs</div>}
      </div>
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        <form className="toolbar" onSubmit={e => { e.preventDefault(); setPage(1); load(1,search,filter); }} style={{ flex:1 }}>
          <input className="search-input" placeholder="Search by name, country..." value={search} onChange={e=>setSearch(e.target.value)} />
          <button type="submit" className="btn btn-ghost">Search</button>
          {search && <button type="button" className="btn btn-ghost" onClick={() => { setSearch(''); load(1,'',filter); }}>Clear</button>}
        </form>
        <select className="input" value={filter} onChange={e => { setFilter(e.target.value); load(1,search,e.target.value); }} style={{ width:'auto', padding:'8px 12px', fontSize:12 }}>
          <option value="">All Status</option>
          <option value="verified">Verified</option>
          <option value="pending">Pending</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>
      <div className="card" style={{ padding:0 }}>
        <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span className="card-title">All Clubs</span>
          {pagination && <span style={{ fontSize:12, color:'var(--text-dim)' }}>Page {pagination.page} of {pagination.totalPages} · {pagination.total} total</span>}
        </div>
        {loading ? <div style={{ padding:60, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>Loading clubs...</div>
        : clubs.length === 0 ? <div style={{ padding:60, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>No clubs found</div>
        : (
          <div className="table-wrap"><table>
            <thead><tr><th>Club</th><th>Country</th><th>Tier</th><th>Rating</th><th>Verification</th><th>Joined</th><th></th></tr></thead>
            <tbody>{clubs.map(c => {
              const vs = c.verificationStatus||c.status||'pending';
              return (
                <tr key={c.id}>
                  <td><div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:32, height:32, borderRadius:6, flexShrink:0, overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', background: c.logoUrl?'transparent':'linear-gradient(135deg,#4B9EF5,#1a5db5)', fontSize:11, fontWeight:700, color:'#fff' }}>
                      {c.logoUrl ? <img src={c.logoUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : initials(c.clubName||c.name)}
                    </div>
                    <div><div style={{ fontWeight:500, fontSize:13 }}>{c.clubName||c.name}</div><div style={{ fontSize:11, color:'var(--text-dim)' }}>{c.contactEmail||c.email}</div></div>
                  </div></td>
                  <td style={{ fontSize:12, color:'var(--text-muted)' }}>{c.clubCountry||c.country||'—'}</td>
                  <td style={{ fontSize:12, color:'var(--text-muted)' }}>{c.ratingTier||c.clubTier||'—'}</td>
                  <td><span style={{ fontSize:12, fontWeight:700, color: c.totalScore>=80?'#A7F432':c.totalScore>=50?'#F5A623':'#ff6b6b' }}>{c.totalScore||'—'}</span></td>
                  <td><span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, background: vs==='verified'?'rgba(167,244,50,0.12)':vs==='suspended'?'rgba(255,80,80,0.12)':'rgba(245,166,35,0.12)', color: vs==='verified'?'#A7F432':vs==='suspended'?'#ff6b6b':'#F5A623' }}>{vs}</span></td>
                  <td style={{ fontSize:12, color:'var(--text-dim)' }}>{fmtDate(c.createdAt)}</td>
                  <td><button className="btn btn-ghost" style={{ fontSize:11, padding:'5px 12px' }} onClick={() => setSelectedId(c.id)}>View</button></td>
                </tr>
              );
            })}</tbody>
          </table></div>
        )}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:16 }}>
          <button className="btn btn-ghost" disabled={page<=1} onClick={() => { const np=page-1; setPage(np); load(np,search,filter); }}>Prev</button>
          <span style={{ padding:'8px 14px', fontSize:13, color:'var(--text-muted)' }}>Page {page} / {pagination.totalPages}</span>
          <button className="btn btn-ghost" disabled={page>=pagination.totalPages} onClick={() => { const np=page+1; setPage(np); load(np,search,filter); }}>Next</button>
        </div>
      )}
      {selectedId && <ClubModal id={selectedId} token={token} onClose={() => setSelectedId(null)} toast={toast} />}
    </div>
  );
}
